import { TFunction, Trans } from 'next-i18next'
import styled from 'styled-components'

import { Broker } from '@/types'
import { breakpoints, colors } from '@/styles'
import isVerified from '@/utils/is-verified'
import CdnImage from '@/components/cdn-image'
import { createModal } from '@/components/modalize'
import { ParagraphRegular } from '@/components/text'
import { Tooltip } from '@/components/tooltip'
import VerifiedBadge from '@/components/verified-badge'

import { ReactComponent as DefaultAvatar } from './default-avatar.svg'

// Styles
// Modal split into Left and Right sections

const Wrapper = styled.div`
  font-size: 16px;
  line-height: 20px;
  font-family: 'Inter';
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  padding: 24px;
  box-shadow: inset 0px -1.5px 0px rgba(0, 0, 0, 0.1);
`

const ModalContent = styled.div`
  display: flex;
  padding: 24px;
  gap: 40px;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
  }
`

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
`

const RightContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`

const ProfileCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  width: 224px;
  border: 1px solid ${colors.grey88};

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: 100%;
  }
`

const LogoContainer = styled.div`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 8px 8px 0 0;
  background-color: ${colors.background};
`

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border-top: 1px solid ${colors.grey88};
`

const LandlordName = styled(ParagraphRegular)`
  color: ${colors.black};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`

const CompanyName = styled(ParagraphRegular)`
  font-size: 14px;
  line-height: 16px;
  color: ${colors.grey48};
`

const SectionTitle = styled(ParagraphRegular)`
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  color: ${colors.black};
  margin-bottom: 8px;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
`

const SectionRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;

  :not(:last-child) {
    border-bottom: 1px solid ${colors.grey88};
  }
`

const InfoKey = styled(ParagraphRegular)`
  line-height: 20px;
  color: ${colors.grey48};
  font-weight: 500;
  flex: 1;
`

const InfoValue = styled(InfoKey)`
  color: ${colors.black};
  word-break: break-word;

  a {
    color: ${colors.link};
    text-decoration: none;
  }
`

type Props = {
  landlord: {
    id: string
    picture: string
    preferred_name: string
    first_name: string
    last_name: string
    about_me: string
    broker: Broker
    verified: boolean
    work_history_year_started: string
    work_history_properties_managed: string
    company: {
      name: string
      email: string
      address: string
      state: string
      city: string
      account_type: string
      unit_number: string
      zip: string
      base_plan_name: string
    }
  }
  t: TFunction
}

const abbreviate = (str: string, count = 1) => `${str.substring(0, count)}.`

const LandlordProfileModal = ({ landlord, t }: Props) => {
  const displayName = landlord.preferred_name || landlord.first_name
  const name = `${landlord.preferred_name} ${abbreviate(landlord.last_name)}`
  const isBrokerAvailable = !!landlord.broker
  const logo = landlord.picture
  const {
    company: { base_plan_name = '' }
  } = landlord

  const isEssentialLandlord =
    !base_plan_name || base_plan_name === 'essentials-plan-monthly'

  const getAddress = () => {
    const {
      company: { address = '', city = '', state = '', zip = '' }
    } = landlord
    const unit = landlord.company.unit_number
      ? `${landlord.company.unit_number} -`
      : ''

    return [unit, address, city, state, zip].join(' ')
  }

  return (
    <Wrapper>
      <ModalHeader>{t('common.profile', 'Profile')}</ModalHeader>

      <ModalContent>
        {/* Left side of modal */}
        <LeftContent>
          <ProfileCard>
            <LogoContainer>
              {logo ? (
                <CdnImage
                  src={logo as string}
                  layout="intrinsic"
                  width={224}
                  height={224}
                  placeholder="blur"
                  objectFit="cover"
                  alt={landlord.preferred_name}
                />
              ) : (
                <DefaultAvatar width={224} height={224} />
              )}
            </LogoContainer>
            <ProfileInfo>
              <LandlordName>
                {name}
                <Tooltip
                  text={t(
                    'b.listing.view.id_verified.tooltip',
                    'This user is ID / Business Licence verified.'
                  )}
                  position="bottom"
                >
                  <VerifiedBadge
                    verified={isVerified(landlord, landlord.company)}
                    type="id"
                    height={22}
                    width={22}
                    bottom={2}
                  />
                </Tooltip>
              </LandlordName>
              {landlord.company.name && (
                <CompanyName>{landlord.company.name}</CompanyName>
              )}
            </ProfileInfo>
          </ProfileCard>
        </LeftContent>

        {/* Right side of modal */}
        <RightContent>
          <Section>
            <SectionTitle>
              <Trans
                t={t}
                i18nKey="r.landlord.view.landlord_story.text"
                values={{ landlordName: displayName }}
                defaults="{{ landlordName }}'s Story"
              />
            </SectionTitle>
            <InfoValue>{landlord.about_me}</InfoValue>
          </Section>
          {/* Only show company info if company exists */}
          {landlord.company && (
            <>
              <Section>
                <SectionTitle>
                  {t('r.landlord.view.company_info.title', 'Company Info')}
                </SectionTitle>
                <SectionRow>
                  <InfoKey>
                    {t('r.landlord.view.company_name.text', 'Company Name')}
                  </InfoKey>
                  <InfoValue>{landlord.company.name}</InfoValue>
                </SectionRow>
                <SectionRow>
                  <InfoKey>
                    {t('r.landlord.view.contact.title', 'Contact')}
                  </InfoKey>
                  <InfoValue>
                    <a
                      href={`mailto:${landlord.company.email}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {landlord.company.email}
                    </a>
                  </InfoValue>
                </SectionRow>
                <SectionRow>
                  <InfoKey>
                    {t('r.landlord.view.address.title', 'Address')}
                  </InfoKey>
                  <InfoValue>{getAddress()}</InfoValue>
                </SectionRow>
                {isBrokerAvailable && (
                  <SectionRow>
                    <InfoKey>
                      {t('r.landlord.view.brokerage.label', 'Brokerage')}
                    </InfoKey>
                    <InfoValue>{landlord.broker.name}</InfoValue>
                  </SectionRow>
                )}
                {!isEssentialLandlord && (
                  <SectionRow>
                    <InfoKey>
                      {t(
                        'r.landlord.view.business_license.title',
                        'Business Licence'
                      )}
                    </InfoKey>
                    <InfoValue>{t('common.verified', 'Verified')}</InfoValue>
                  </SectionRow>
                )}
              </Section>
              <Section>
                <SectionTitle>
                  {t('r.landlord.view.work_history.title', 'Work History')}
                </SectionTitle>
                <SectionRow>
                  <InfoKey>
                    {t('r.landlord.view.year_started.text', 'Year Started')}
                  </InfoKey>
                  <InfoValue>
                    {landlord.work_history_year_started || '-'}
                  </InfoValue>
                </SectionRow>
                <SectionRow>
                  <InfoKey>
                    {t(
                      'r.landlord.view.properties_managed.text',
                      'Properties Managed'
                    )}
                  </InfoKey>
                  <InfoValue>
                    {landlord.work_history_properties_managed || '-'}
                  </InfoValue>
                </SectionRow>
              </Section>
            </>
          )}
        </RightContent>
      </ModalContent>
    </Wrapper>
  )
}

export const showLandlordProfileModal = createModal(LandlordProfileModal, {
  maxWidth: '704px',
  width: '100%',
  closeButtonTop: '24px'
})
